// features/super-admin/pages/SuperAdminDashboard.tsx
/**
 * SYSTEM OWNER DASHBOARD — Platform command center.
 * Pure system administration — no tenant features.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    Building2, Users, Store,
    Activity, Zap, Layers, Shield, CreditCard,
    Star, Award, CheckCircle2,
    FileText, Server,
    ChevronRight,
    DollarSign, TrendingUp, AlertTriangle, Cpu, Clock,
    Search, MessageSquare, ExternalLink, ShieldCheck,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface ExpiringTenant {
    id: string;
    name: string;
    slug: string;
    email: string | null;
    phone: string | null;
    subscriptionTier: string;
    subscriptionExpiresAt: string;
}

interface DashboardMetrics {
    totalTenants: number;
    activeTenants: number;
    totalBusinesses: number;
    totalUsers: number;
    recentSignups: number;
    mrr?: number;
    arr?: number;
    expiringSoon?: ExpiringTenant[];
}

interface SystemHealth {
    status: string;
    database: { status: string; latencyMs: number };
    uptime: { seconds: number; formatted: string };
    memory: { rssMb: number; heapUsedMb: number; heapTotalMb: number };
    nodeVersion: string;
    totalTenants: number;
    totalUsers: number;
    timestamp: string;
}

interface PlanDistribution {
    free: number;
    starter: number;
    business: number;
    enterprise: number;
}

interface RecentActivity {
    id: string;
    tenantName: string;
    action: string;
    createdAt: string;
}

interface ErrorLogStats {
    totalErrors: number;
    criticalErrors: number;
    serverErrors: number;
    validationErrors: number;
    todayErrors: number;
}

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '₦0';
    if (num >= 1_000_000) return `₦${(num / 1_000_000).toFixed(1)}M`;
    return `₦${num.toLocaleString('en-NG')}`;
};

const formatDate = (date: string) => {
    try {
        return new Date(date).toLocaleDateString('en-NG', {
            day: 'numeric', month: 'short', year: 'numeric',
        });
    } catch {
        return 'N/A';
    }
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

function StatCard({
    label, value, icon: Icon, gradient, onClick, subtitle,
}: {
    label: string; value: string | number; icon: React.ElementType;
    gradient: string; onClick?: () => void; subtitle?: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            whileHover={onClick ? { y: -3 } : undefined}
            onClick={onClick}
            className={clsx(
                'bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-lg transition-all',
                onClick && 'cursor-pointer'
            )}
        >
            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md w-fit mb-3`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-[10px] text-slate-400 mt-1">{subtitle}</p>}
        </motion.div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function SuperAdminDashboard() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);

    const [quickSearch, setQuickSearch] = useState('');

    const { data: metrics, isLoading } = useQuery<DashboardMetrics>({
        queryKey: ['super-admin-dashboard'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/dashboard');
            return data?.data;
        },
        enabled: !!user,
    });

    const { data: systemHealth } = useQuery<SystemHealth>({
        queryKey: ['super-admin-system-health'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/system/health');
            return data?.data;
        },
        enabled: !!user,
        refetchInterval: 15000,
    });

    const { data: planDist } = useQuery<PlanDistribution>({
        queryKey: ['super-admin-plans-distribution'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/dashboard/plans');
            return data?.data;
        },
        enabled: !!user,
        retry: 1,
    });

    const { data: recentActivity } = useQuery<RecentActivity[]>({
        queryKey: ['super-admin-activity'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/dashboard/activity');
            return data?.data || [];
        },
        enabled: !!user,
        retry: 1,
    });

    const { data: errorStats } = useQuery<ErrorLogStats>({
        queryKey: ['super-admin-error-stats'],
        queryFn: async () => {
            const { data } = await apiClient.get('/error-logs/stats');
            return data?.data;
        },
        enabled: !!user,
        retry: 1,
    });

    const totalTenants = metrics?.totalTenants || 0;
    const activeTenants = metrics?.activeTenants || 0;
    const tenantHealthPercent = totalTenants > 0 ? Math.round((activeTenants / totalTenants) * 100) : 100;
    const totalPlanCount = planDist
        ? planDist.free + planDist.starter + planDist.business + planDist.enterprise
        : 0;

    const handleQuickSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (quickSearch.trim()) {
            navigate(`/super-admin/tenants?search=${encodeURIComponent(quickSearch.trim())}`);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[80vh]">
                <Spinner size="xl" variant="emerald" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-5">

                {/* ========== HERO HEADER ========== */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-violet-950 to-indigo-950 rounded-3xl shadow-2xl"
                >
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.3),transparent_50%)]" />
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-violet-500/20 rounded-full blur-3xl" />
                    </div>
                    <div className="relative px-8 lg:px-12 py-8 lg:py-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-2xl ring-4 ring-violet-500/20">
                                    <Zap size={32} className="text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Badge variant="premium" size="sm">System Owner</Badge>
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                            Platform Online
                                        </span>
                                    </div>
                                    <h1 className="text-3xl lg:text-4xl font-black text-white">
                                        EtiPOS Command Center
                                    </h1>
                                    <p className="text-violet-200 text-sm mt-1">
                                        {totalTenants} organizations · {activeTenants} active · {metrics?.totalUsers || 0} total platform users
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <form onSubmit={handleQuickSearchSubmit} className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                                    <input
                                        type="text"
                                        placeholder="Jump to tenant..."
                                        value={quickSearch}
                                        onChange={(e) => setQuickSearch(e.target.value)}
                                        className="pl-8 pr-3 py-2 bg-white/10 border border-white/20 rounded-xl text-xs text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 w-44 lg:w-56"
                                    />
                                </form>
                                <button onClick={() => navigate('/super-admin/audit-logs')}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl hover:bg-white/20 transition-all text-sm font-medium">
                                    <FileText size={14} /> Audit Logs
                                </button>
                                <button onClick={() => navigate('/super-admin/error-logs')}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white border border-rose-200/30 rounded-xl hover:bg-white/20 transition-all text-sm font-medium">
                                    <Server size={14} /> Error Logs
                                    {errorStats && errorStats.criticalErrors > 0 && (
                                        <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                                    )}
                                </button>
                                <button onClick={() => navigate('/super-admin/subscriptions')}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl hover:bg-white/20 transition-all text-sm font-medium">
                                    <CreditCard size={14} /> Billing
                                </button>
                                <button onClick={() => navigate('/super-admin/tenants')}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-violet-700 rounded-xl hover:bg-violet-50 transition-all font-semibold shadow-xl text-sm">
                                    <Building2 size={16} /> Manage Tenants
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* ========== LIVE SYSTEM TELEMETRY STRIP ========== */}
                {systemHealth && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-slate-200/80 shadow-sm px-5 py-3.5 flex flex-wrap items-center justify-between gap-4"
                    >
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-500" />
                            <span className="text-xs font-bold text-slate-800">Platform Telemetry:</span>
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-semibold text-[11px] border border-emerald-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                All Systems Operational
                            </span>
                        </div>
                        <div className="flex items-center gap-6 text-xs text-slate-500">
                            <div className="flex items-center gap-1.5">
                                <Cpu size={13} className="text-slate-400" />
                                <span>DB Query Latency:</span>
                                <strong className="text-slate-800 font-bold">{systemHealth.database.latencyMs}ms</strong>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Clock size={13} className="text-slate-400" />
                                <span>Server Uptime:</span>
                                <strong className="text-slate-800 font-bold">{systemHealth.uptime.formatted}</strong>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Server size={13} className="text-slate-400" />
                                <span>Heap Memory:</span>
                                <strong className="text-slate-800 font-bold">{systemHealth.memory.heapUsedMb} MB</strong>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* ========== EXPIRING SUBSCRIPTIONS ATTENTION BANNER ========== */}
                {metrics?.expiringSoon && metrics.expiringSoon.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-amber-50/80 border-2 border-amber-200 rounded-2xl p-4 shadow-sm"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
                                <AlertTriangle size={18} className="text-amber-600" />
                                <span>{metrics.expiringSoon.length} Tenant Subscription(s) Expiring Within 7 Days</span>
                            </div>
                            <button
                                onClick={() => navigate('/super-admin/subscriptions')}
                                className="text-xs font-bold text-amber-700 hover:text-amber-800 underline flex items-center gap-1"
                            >
                                View All Subscriptions <ChevronRight size={12} />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                            {metrics.expiringSoon.map((exp) => (
                                <div key={exp.id} className="bg-white rounded-xl p-3 border border-amber-200/70 shadow-sm flex items-center justify-between">
                                    <div className="min-w-0 pr-2">
                                        <p className="font-bold text-xs text-slate-800 truncate">{exp.name}</p>
                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                            Expires: {formatDate(exp.subscriptionExpiresAt)} · Plan: <span className="uppercase font-bold text-violet-600">{exp.subscriptionTier}</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {exp.phone && (
                                            <a
                                                href={`https://wa.me/${exp.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello ${exp.name}, your EtiPOS subscription is expiring soon on ${formatDate(exp.subscriptionExpiresAt)}. Please reach out to renew your license.`)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition"
                                                title="Send WhatsApp Renewal Reminder"
                                            >
                                                <MessageSquare size={14} />
                                            </a>
                                        )}
                                        <button
                                            onClick={() => navigate(`/super-admin/tenants/${exp.id}`)}
                                            className="px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 text-[11px] font-bold transition"
                                        >
                                            Manage
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* ========== KEY METRICS WITH MRR & ARR ========== */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard label="Total Tenants" value={totalTenants.toLocaleString()}
                        icon={Building2} gradient="from-violet-500 to-purple-600"
                        onClick={() => navigate('/super-admin/tenants')} />
                    <StatCard label="Active Tenants" value={activeTenants.toLocaleString()}
                        icon={CheckCircle2} gradient="from-emerald-500 to-teal-600" />
                    <StatCard label="Monthly Rev (MRR)" value={formatNaira(metrics?.mrr || 0)}
                        icon={DollarSign} gradient="from-emerald-600 to-teal-700" subtitle="Platform recurring" />
                    <StatCard label="Annual Run-Rate" value={formatNaira(metrics?.arr || 0)}
                        icon={TrendingUp} gradient="from-indigo-600 to-blue-700" subtitle="Projected ARR" />
                    <StatCard label="Total Businesses" value={(metrics?.totalBusinesses || 0).toLocaleString()}
                        icon={Store} gradient="from-blue-500 to-indigo-600" />
                    <StatCard label="Total Users" value={(metrics?.totalUsers || 0).toLocaleString()}
                        icon={Users} gradient="from-purple-500 to-violet-600" />
                </div>

                {/* ========== ERROR LOGS QUICK STATS ========== */}
                {errorStats && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border-2 border-rose-100 shadow-sm p-5 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => navigate('/super-admin/error-logs')}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 shadow-md">
                                    <Server className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-800">System Error Monitoring</h3>
                                    <p className="text-xs text-slate-500">Click to view all error logs</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-300" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="p-3 bg-slate-50 rounded-xl text-center">
                                <p className="text-[10px] text-slate-400 uppercase font-bold">Total Errors</p>
                                <p className="text-lg font-black text-slate-700">{errorStats.totalErrors.toLocaleString()}</p>
                            </div>
                            <div className="p-3 bg-rose-50 rounded-xl text-center">
                                <p className="text-[10px] text-rose-400 uppercase font-bold">Critical</p>
                                <p className="text-lg font-black text-rose-600">{errorStats.criticalErrors.toLocaleString()}</p>
                            </div>
                            <div className="p-3 bg-red-50 rounded-xl text-center">
                                <p className="text-[10px] text-red-400 uppercase font-bold">Server Errors</p>
                                <p className="text-lg font-black text-red-600">{errorStats.serverErrors.toLocaleString()}</p>
                            </div>
                            <div className="p-3 bg-amber-50 rounded-xl text-center">
                                <p className="text-[10px] text-amber-400 uppercase font-bold">Validation</p>
                                <p className="text-lg font-black text-amber-600">{errorStats.validationErrors.toLocaleString()}</p>
                            </div>
                            <div className="p-3 bg-blue-50 rounded-xl text-center">
                                <p className="text-[10px] text-blue-400 uppercase font-bold">Today</p>
                                <p className="text-lg font-black text-blue-600">{errorStats.todayErrors.toLocaleString()}</p>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* ========== TENANT HEALTH + PLAN DISTRIBUTION ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* Tenant Health */}
                    <Card>
                        <CardHeader gradient>
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <Shield size={18} className="text-emerald-500" /> Tenant Health
                            </h3>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-6">
                                <div className="relative w-[100px] h-[100px] flex-shrink-0">
                                    <svg className="w-[100px] h-[100px] -rotate-90" viewBox="0 0 68 68">
                                        <circle cx="34" cy="34" r="30" fill="none" stroke="#f1f5f9" strokeWidth="6" />
                                        <motion.circle cx="34" cy="34" r="30" fill="none"
                                            stroke="url(#tenant-health)" strokeWidth="6" strokeLinecap="round"
                                            initial={{ strokeDashoffset: 188.5 }}
                                            animate={{ strokeDashoffset: 188.5 - (tenantHealthPercent / 100) * 188.5 }}
                                            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                                            strokeDasharray={188.5} />
                                        <defs>
                                            <linearGradient id="tenant-health" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#10b981" /><stop offset="100%" stopColor="#14b8a6" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-2xl font-black text-emerald-600">{tenantHealthPercent}%</span>
                                    </div>
                                </div>
                                <div className="space-y-3 flex-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Active</span>
                                        <span className="font-bold text-emerald-600">{activeTenants}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Inactive</span>
                                        <span className="font-bold text-rose-500">{totalTenants - activeTenants}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Total</span>
                                        <span className="font-bold text-slate-700">{totalTenants}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">New (30d)</span>
                                        <span className="font-bold text-blue-600">+{metrics?.recentSignups || 0}</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Plan Distribution */}
                    <Card>
                        <CardHeader gradient>
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <Layers size={18} className="text-violet-500" /> Plan Distribution
                            </h3>
                        </CardHeader>
                        <CardContent>
                            {planDist ? (
                                <div className="space-y-4">
                                    {([
                                        { label: 'Free', value: planDist.free, color: 'bg-slate-500', pct: totalPlanCount > 0 ? (planDist.free / totalPlanCount) * 100 : 0, icon: Star },
                                        { label: 'Starter', value: planDist.starter, color: 'bg-blue-500', pct: totalPlanCount > 0 ? (planDist.starter / totalPlanCount) * 100 : 0, icon: Zap },
                                        { label: 'Business', value: planDist.business, color: 'bg-purple-500', pct: totalPlanCount > 0 ? (planDist.business / totalPlanCount) * 100 : 0, icon: Building2 },
                                        { label: 'Enterprise', value: planDist.enterprise, color: 'bg-emerald-500', pct: totalPlanCount > 0 ? (planDist.enterprise / totalPlanCount) * 100 : 0, icon: Award },
                                    ]).map((p) => (
                                        <div key={p.label} className="flex items-center gap-3">
                                            <div className="flex items-center gap-2 w-24">
                                                <p.icon size={14} className="text-slate-400" />
                                                <span className="text-xs text-slate-600 font-medium">{p.label}</span>
                                            </div>
                                            <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${p.pct}%` }}
                                                    transition={{ duration: 1 }}
                                                    className={`h-6 rounded-full ${p.color} flex items-center justify-end pr-2`}
                                                >
                                                    {p.pct > 10 && (
                                                        <span className="text-[10px] font-bold text-white">{p.value}</span>
                                                    )}
                                                </motion.div>
                                            </div>
                                            <span className="text-[10px] text-slate-400 w-10 text-right font-medium">{p.pct.toFixed(0)}%</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-sm text-slate-400">Loading plan data...</div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* ========== RECENT ACTIVITY ========== */}
                <Card>
                    <CardHeader gradient>
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <Activity size={18} className="text-amber-500" /> Recent Platform Activity
                            </h3>
                            <Badge variant="info" size="sm">Live</Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {!recentActivity || recentActivity.length === 0 ? (
                            <div className="text-center py-8">
                                <Activity size={32} className="text-slate-300 mx-auto mb-3" />
                                <p className="text-sm text-slate-400">Activity feed loading...</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                                {recentActivity.slice(0, 8).map((activity, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                                        <div className="p-1.5 rounded-lg bg-violet-100 flex-shrink-0 mt-0.5">
                                            <Building2 size={12} className="text-violet-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-bold text-slate-700 truncate">{activity.tenantName}</p>
                                            <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(activity.createdAt)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <button onClick={() => navigate('/super-admin/audit-logs')}
                            className="w-full mt-4 text-sm text-violet-600 font-medium hover:text-violet-700 py-2 flex items-center justify-center gap-1">
                            View All Audit Logs <ChevronRight size={14} />
                        </button>
                    </CardContent>
                </Card>

                {/* ========== SUPER ADMIN ACTIONS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                        { label: 'Manage Tenants', desc: 'View and manage all organizations', icon: Building2, path: '/super-admin/tenants', gradient: 'from-violet-500 to-purple-600' },
                        { label: 'Subscriptions & Billing', desc: 'Manage plans and invoices', icon: CreditCard, path: '/super-admin/subscriptions', gradient: 'from-emerald-500 to-teal-600' },
                        { label: 'Feature Flags', desc: 'Configure plan features', icon: Shield, path: '/super-admin/plans', gradient: 'from-blue-500 to-indigo-600' },
                        { label: 'Audit Logs', desc: 'System-wide activity trail', icon: FileText, path: '/super-admin/audit-logs', gradient: 'from-amber-500 to-orange-600' },
                        { label: 'Error Logs', desc: 'Monitor system errors', icon: Server, path: '/super-admin/error-logs', gradient: 'from-rose-500 to-red-600' },
                        { label: 'Plans & Pricing', desc: 'Subscription plan management', icon: Layers, path: '/super-admin/plans', gradient: 'from-cyan-500 to-sky-600' },
                    ].map((action) => (
                        <motion.button
                            key={action.label}
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            whileHover={{ y: -4, scale: 1.02 }} whileTap={{ scale: 0.98 }}
                            onClick={() => navigate(action.path)}
                            className="bg-white rounded-2xl border-2 border-slate-200 p-5 hover:border-violet-300 hover:shadow-lg transition-all group flex items-center gap-4"
                            type="button"
                        >
                            <div className={`p-3 rounded-xl bg-gradient-to-br ${action.gradient} shadow-lg group-hover:scale-110 transition-transform flex-shrink-0`}>
                                <action.icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-left">
                                <h4 className="font-bold text-sm text-slate-800">{action.label}</h4>
                                <p className="text-[10px] text-slate-400 mt-0.5">{action.desc}</p>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>
        </div>
    );
}